import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { api, errText } from "../../lib/admin";
import { Loading, Btn, FilterBar } from "./ui";

export const SLOTS = ["Mattina", "Pausa pranzo", "Pomeriggio", "Sera"];
const EN = { Morning: "Mattina", "Lunch break": "Pausa pranzo", Afternoon: "Pomeriggio", Evening: "Sera" };
export const slotOf = (t) => EN[t] || t || "Da definire";
export const slotFromHour = (h) => { const n = Number(h.slice(0, 2)); return n < 12 ? "Mattina" : n < 15 ? "Pausa pranzo" : n < 18 ? "Pomeriggio" : "Sera"; };
const SLOT_ORDER = { Mattina: "09:00", "Pausa pranzo": "12:30", Pomeriggio: "15:00", Sera: "18:30" };
const sortKey = (a) => a.confirmed_time || `${SLOT_ORDER[slotOf(a.preferred_time)] || "23:00"}~`;
const HOURS = Array.from({ length: 13 }, (_, i) => i + 8);

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monday = (d) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const Appt = ({ a }) => {
  const dark = a.status === "confirmed";
  return (
    <div data-testid={`cal-appt-${a.id}`} className={`rounded-lg px-3 py-2 text-left border ${dark ? "bg-[#14110F] text-[#F6F2EC] border-[#14110F]" : "bg-[#fbf6ec] text-[#14110F] border-dashed border-[#C9A66B]"}`}>
      <p className={`text-[0.7rem] tracking-[0.12em] uppercase ${dark ? "text-[#C9A66B]" : "text-[#8A6A3A]"}`} data-testid={`cal-appt-time-${a.id}`}>{a.confirmed_time ? `ore ${a.confirmed_time}` : slotOf(a.preferred_time)}</p>
      <p className="text-sm font-medium leading-tight mt-0.5">{a.name}</p>
      <p className={`text-xs mt-0.5 ${dark ? "text-[#e6d3ae]" : "text-[#8A6A3A]"}`}>{a.service}</p>
      <a href={`tel:${a.phone}`} className={`mt-1 inline-flex items-center gap-1 text-xs ${dark ? "text-[#c9bfb2]" : "text-[#6b625a]"}`}><Phone className="w-3 h-3" /> {a.phone}</a>
    </div>
  );
};

const WeekDay = ({ d, items, today, onOpen }) => {
  const key = iso(d);
  return (
    <section data-testid={`cal-day-${key}`} className={`bg-white border rounded-xl p-3 min-h-[120px] ${key === today ? "border-[#8A6A3A] ring-1 ring-[#8A6A3A]/30" : "border-[#e7dfd3]"}`}>
      <button type="button" onClick={() => onOpen(d)} className="w-full flex items-baseline justify-between mb-3 group" data-testid={`cal-open-day-${key}`}>
        <span className="text-[0.68rem] tracking-[0.16em] uppercase text-[#6b625a] group-hover:text-[#8A6A3A]">{d.toLocaleDateString("it-IT", { weekday: "short" })}</span>
        <span className={`font-serif-d text-2xl font-light ${key === today ? "text-[#8A6A3A]" : "text-[#14110F]"}`}>{d.getDate()}</span>
      </button>
      {items.length ? <div className="space-y-1.5">{items.map((a) => <Appt key={a.id} a={a} />)}</div> : <p className="text-xs text-[#9b9188]">Libera</p>}
    </section>
  );
};

const DayView = ({ items }) => {
  const timed = items.filter((a) => a.confirmed_time);
  const untimed = items.filter((a) => !a.confirmed_time);
  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="cal-day-view">
      <ol className="lg:col-span-8 bg-white border border-[#e7dfd3] rounded-xl divide-y divide-[#efe9df]">
        {HOURS.map((h) => {
          const hh = String(h).padStart(2, "0");
          const xs = timed.filter((a) => a.confirmed_time.slice(0, 2) === hh);
          return (
            <li key={h} className="grid grid-cols-[4rem_minmax(0,1fr)] gap-3 px-3 py-2 min-h-[56px]" data-testid={`cal-hour-${hh}`}>
              <span className="font-stencil tracking-[0.12em] text-[#8A6A3A] pt-1">{hh}:00</span>
              <div className="grid sm:grid-cols-2 gap-2">{xs.map((a) => <Appt key={a.id} a={a} />)}</div>
            </li>
          );
        })}
      </ol>
      <aside className="lg:col-span-4 space-y-2">
        <p className="text-[0.7rem] tracking-[0.16em] uppercase text-[#6b625a]">Senza ora esatta</p>
        {untimed.length ? untimed.map((a) => <Appt key={a.id} a={a} />) : <p className="text-sm text-[#9b9188]">Nessuno</p>}
      </aside>
    </div>
  );
};

export const CalendarAdmin = () => {
  const [items, setItems] = useState(null);
  const [view, setView] = useState("week");
  const [day, setDay] = useState(new Date());
  const [withPending, setWithPending] = useState(false);
  useEffect(() => {
    api.get("/admin/appointments").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(errText(e)); setItems([]); });
  }, []);
  const start = useMemo(() => monday(day), [day]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(start, i)), [start]);
  if (!items) return <Loading />;
  const visible = items.filter((a) => a.status === "confirmed" || (withPending && (a.status || "pending") === "pending"));
  const on = (d) => visible.filter((a) => a.preferred_date === iso(d)).sort((x, y) => sortKey(x).localeCompare(sortKey(y)));
  const noDate = visible.filter((a) => !a.preferred_date);
  const today = iso(new Date());
  const step = view === "week" ? 7 : 1;
  const label = view === "week"
    ? `${days[0].toLocaleDateString("it-IT", { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}`
    : day.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  const count = view === "week" ? days.reduce((n, d) => n + on(d).length, 0) : on(day).length;
  return (
    <div className="space-y-6" data-testid="admin-calendar">
      <FilterBar value={view} onChange={setView} testPrefix="cal-view" options={[["day", "Giorno"], ["week", "Settimana"]]} />
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setDay(addDays(day, -step))} aria-label="Indietro" className="p-2 rounded-full border border-[#ddd4c7] hover:border-[#8A6A3A]" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
          <p className="font-serif-d text-2xl font-light min-w-[13ch] text-center capitalize" data-testid="cal-label">{label}</p>
          <button type="button" onClick={() => setDay(addDays(day, step))} aria-label="Avanti" className="p-2 rounded-full border border-[#ddd4c7] hover:border-[#8A6A3A]" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
          <Btn variant="ghost" onClick={() => setDay(new Date())} data-testid="cal-today-btn">Oggi</Btn>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-[#4f4841]" data-testid="cal-count">{count} appuntamenti</span>
          <label className="flex items-center gap-2 text-sm text-[#4f4841] cursor-pointer">
            <input type="checkbox" checked={withPending} onChange={(e) => setWithPending(e.target.checked)} className="w-4 h-4 accent-[#8A6A3A]" data-testid="cal-show-pending" /> Mostra anche da confermare
          </label>
        </div>
      </div>
      {view === "week" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
          {days.map((d) => <WeekDay key={iso(d)} d={d} items={on(d)} today={today} onOpen={(x) => { setDay(x); setView("day"); }} />)}
        </div>
      ) : <DayView items={on(day)} />}
      {noDate.length > 0 && (
        <div className="bg-[#fbf6ec] border border-[#ecdcbf] rounded-xl p-4" data-testid="cal-no-date">
          <p className="text-sm text-[#7a5a28] mb-3">{noDate.length} {noDate.length === 1 ? "appuntamento senza data" : "appuntamenti senza data"}: aggiungila da "Prenotazioni" → "Modifica data e ora".</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">{noDate.map((a) => <Appt key={a.id} a={a} />)}</div>
        </div>
      )}
    </div>
  );
};
